import React, { useContext } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { LinePlotMarkOptions, LinePlotQuestionOptions, LinePlotScaleOptions } from "../Constants.ts";
import { labelFor, withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The three line-plot dials (ADR 0084 §4-§6): what to ask, the unit between
 * the ticks, and whether the marks are X's or dots.
 *
 * All three are basic tier, as the format's own panel is (ADR 0023 §2, as
 * ADR 0079 applied it). The operator bar is hidden for this format: a line
 * plot is a data set, and the question type says what to do with it.
 */
export function LinePlotSettings() {
    const { settings, setSettings } = useContext(SettingsContext);
    const t = useTranslation();

    function handleQuestionChange(value: LinePlotQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...settings, LinePlotQuestion: value });
    }

    function handleScaleChange(event) {
        setSettings({ ...settings, LinePlotScale: event.target.value });
    }

    function handleMarkChange(event) {
        setSettings({ ...settings, LinePlotMark: event.target.value });
    }

    return (
        <div className="linePlotSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.LinePlotQuestion}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(LinePlotQuestionOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            <div className="selectContainer">
                <span className="settingLabel">{t('settings.linePlotScaleLabel')}</span>
            </div>
            <div className="radioButtons" role="radiogroup" aria-label={t('settings.linePlotScaleAria')}>
                {Object.values(LinePlotScaleOptions).map((option) => (
                    <div key={option}>
                        <RadioButton
                            inputId={`linePlotScale-${option}`}
                            value={option}
                            checked={settings.LinePlotScale === option}
                            onChange={handleScaleChange}
                        />
                        <label htmlFor={`linePlotScale-${option}`}>{labelFor(option, settings.Language)}</label>
                    </div>
                ))}
            </div>
            <div className="selectContainer">
                <span className="settingLabel">{t('settings.linePlotMarkLabel')}</span>
            </div>
            <div className="radioButtons" role="radiogroup" aria-label={t('settings.linePlotMarkAria')}>
                {Object.values(LinePlotMarkOptions).map((option) => (
                    <div key={option}>
                        <RadioButton
                            inputId={`linePlotMark-${option}`}
                            value={option}
                            checked={settings.LinePlotMark === option}
                            onChange={handleMarkChange}
                        />
                        <label htmlFor={`linePlotMark-${option}`}>{labelFor(option, settings.Language)}</label>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default LinePlotSettings;
